// manage-payment-methods-dialog.component.ts
import { Component, Inject, ViewChild, TemplateRef, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialog
} from '@angular/material/dialog';
import { MatFormFieldModule }    from '@angular/material/form-field';
import { MatInputModule }        from '@angular/material/input';
import { MatSelectModule }       from '@angular/material/select';
import { MatListModule }         from '@angular/material/list';
import { MatButtonModule }       from '@angular/material/button';
import { MatRadioModule }        from '@angular/material/radio';


import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule }         from '@angular/material/icon';
import { Bank }                  from '../../models/bank.model';
import { ConfirmationDialogComponent, ConfirmationDialogData }
  from '../confirmation-dialog/confirmation-dialog.component';
  import { PaymentService } from '../../services/payment.service';  // ← import
  import { BankService } from '../../services/bank.service';  // ← import
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
  import { finalize } from 'rxjs/operators';


@Component({
  selector: 'app-manage-payment-methods-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatListModule,
    MatButtonModule,
    MatRadioModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatSnackBarModule
],
  templateUrl: './manage-payment-methods-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./manage-payment-methods-dialog.component.scss']
})
export class ManagePaymentMethodsDialogComponent {
  @ViewChild('removeConfirm') removeConfirmTpl!: TemplateRef<any>;

  /** full Bank objects */
  accounts: Bank[] = [];
  selectedAccount;
  addForm: FormGroup;
  accountToRemove: Bank | null = null;
  isSaving = false;   // track loading state
  isDeleting = false;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<ManagePaymentMethodsDialogComponent>,
    private dialog: MatDialog,
        private paymentService: PaymentService,            // ← inject
            private bankService: BankService,    // ← inject BankService
  private snackBar: MatSnackBar,   // ⬅️ add this

    @Inject(MAT_DIALOG_DATA) public data: { accounts: Bank[] }
  ) {
    this.accounts = data.accounts;
    this.selectedAccount = this.fb.control<string | null>(null);
    this.addForm = this.fb.group({
  routingNumber: [
    '',
    [
      Validators.required,
      Validators.pattern(/^\d{9}$/), // exactly 9 digits
      Validators.maxLength(9)        // prevents > 9 chars
    ]
  ],
        accountNumber: ['', Validators.required],
      confirmAccountNumber: ['', Validators.required],
      accountType: ['', Validators.required],
      nickname: ['']
    }, { validators: this.matchAccountNumbers });
  }

  private matchAccountNumbers(group: FormGroup) {
    const acct    = group.get('accountNumber')?.value;
    const confirm = group.get('confirmAccountNumber')?.value;
    return acct && confirm && acct === confirm ? null : { mismatch: true };
  }

 confirmRemove(acct: Bank) {
    this.accountToRemove = acct;

    // Build the data payload for the generic confirmation dialog
    const data: ConfirmationDialogData = {
  confirmType: 'bankRemoval',
  title: 'Remove Bank Account?',
  message: 'Are you sure you want to remove this bank account?',
  nickname: acct.Nickname,
  last4: ('' + acct.AccountNumber).slice(-4),
  confirmText: 'Yes, remove',
  cancelText: 'No, keep it'
    }

    // Open the reusable ConfirmationDialogComponent
    const ref = this.dialog.open(ConfirmationDialogComponent, {
      data,
      width: '400px'
    });

    // If the user confirms, do the remove
    ref.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.removeAccount();
      } else {
        this.accountToRemove = null;
      }
    });
  }

removeAccount() {
    if (!this.accountToRemove) return;

    const walletId = this.accountToRemove.ACHWalletID;
    if (walletId === undefined || walletId === null) {
      console.error('Cannot delete wallet: ACHWalletID is undefined or null');
      this.accountToRemove = null;
      return;
    }
    this.isDeleting = true;

    this.bankService
      .deleteWallet(walletId)            // ← call BankService.deleteWallet(...)
      .pipe(finalize(() => {
        this.isDeleting = false;
        this.accountToRemove = null;
      }))
      .subscribe({
        next: () => {
          // remove from local list so UI updates
          this.accounts = this.accounts.filter(b => b.ACHWalletID !== walletId);
          // immediately return updated list so caller can resync
          this.finishAndReturnUpdatedAccounts();        },
        error: err => {
          console.error('Failed to delete wallet', err);
          // TODO: show a snack-bar or inline error
        }
      });
  }


  closeRemove() {
    this.dialog.closeAll();
    this.accountToRemove = null;
  }

  close() {
    this.dialogRef.close();
  }

  private finishAndReturnUpdatedAccounts() {
  this.dialogRef.close(this.accounts);
}

save() {
    if (this.addForm.invalid) {
      return;
    }

    this.isSaving = true;

    const form = this.addForm.value;

    const rawType: 'checking'|'savings' = form.accountType;
    const accountTypeNum = rawType === 'checking' ? 0 : 1;

    const walletData = {
      AccountType: accountTypeNum,                  // 0 = checking, 1 = savings, etc
      Nickname:    form.nickname || 'New Bank',
      AccountNumber: form.accountNumber,
      ABANumber:     form.routingNumber,
      Validated:     0,                                // or whatever your flow dictates
      Active:        1,
    };

    this.paymentService
      .createWallet(walletData)
      .pipe(finalize(() => (this.isSaving = false)))
    .subscribe({
      next: () => {
        // optionally push a “fake” Bank model if you want to show it immediately
        const newBank: Bank = { 
          ACHWalletID: Math.random(), 
          bankName: walletData.Nickname,
          AccountNumber: +walletData.AccountNumber,
          routingNumber: walletData.ABANumber 
        };
        this.accounts.push(newBank);
        this.dialogRef.close(newBank);
      },
error: err => {
  console.error('Failed to save bank:', err);

  // Decide which toast message to show
  let toastMsg = 'An error has occurred.';
  const backendMsg = err?.error?.message || '';

  if (backendMsg.toLowerCase().includes('abanumber')) {
    toastMsg = 'Invalid Routing Number';
  }

this.snackBar.open(toastMsg, 'Dismiss', {
  duration: 6000,
  horizontalPosition: 'right',
  verticalPosition: 'bottom',
  panelClass: ['error-snackbar']  // 👈 add custom style
});

}

      });
  }
}
